import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "./Components/Header";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import TutorProfileCard from "./Components/TutorProfileCard";
import QualificationsCard from "./Components/QualificationsCard";
import SpecializationsCard from "./Components/SpecializationsCard";
import TutorSidebar from "./Components/TutorSidebar";
import {
  getTeacherDisplayName,
  normalizeAvailabilityCollection,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  normalizeAudPriceForDisplay,
  normalizeAudPriceToMinorUnits,
} from "../../utils/audCurrency";
import { formatTeacherYearLabels } from "../../utils/teacherYears";
import { getClassBookingQueryErrorMessage } from "../../utils/uiErrorMessages";
import { getClassBookingBasePath } from "../../features/parentFlow/parentFlow";
import { mergePendingTutorSlotsFromStorage } from "./pendingTutorAvailability";

const buildAvatarDataUri = (name = "Teacher") => {
  const safeName = String(name || "Teacher").trim() || "Teacher";
  const initial = safeName.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f97316" />
          <stop offset="100%" stop-color="#ef4444" />
        </linearGradient>
      </defs>
      <rect width="128" height="128" rx="24" fill="url(#bg)" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="48" font-weight="700" fill="white">
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const toList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isFutureAvailabilitySlot = (slot, now = new Date()) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  if (!startDate) return false;
  return startDate.getTime() > now.getTime();
};

const TutorDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tutorId } = useParams();
  const bookingBasePath = getClassBookingBasePath(location.pathname);
  const isParentFlow = bookingBasePath.startsWith("/parent/");

  const tutorDetailQuery = useQuery({
    queryKey: ["class-booking-tutor-detail", tutorId],
    queryFn: () => teacherApi.getTutorDetail(tutorId),
    enabled: Boolean(tutorId),
    retry: false,
  });

  const tutorAvailabilityQuery = useQuery({
    queryKey: ["class-booking-tutor-availability", tutorId],
    queryFn: () => teacherApi.getTutorAvailability(tutorId),
    enabled: Boolean(tutorId) && !tutorDetailQuery.isError,
    retry: false,
  });

  const tutor = useMemo(() => {
    const teacher = tutorDetailQuery.data || {};
    const name = getTeacherDisplayName(teacher);
    const experience = toNumber(teacher?.experience);
    const rating = toNumber(teacher?.rating);
    const reviewCount = toNumber(
      teacher?.reviews ?? teacher?.review_count ?? teacher?.total_reviews
    );
    const rawPricePerSession = teacher?.price_per_session ?? teacher?.price;
    const pricePerSession = normalizeAudPriceForDisplay(rawPricePerSession);

    return {
      id: teacher?.id || tutorId,
      name,
      image:
        teacher?.photoImage ||
        teacher?.photo ||
        teacher?.image ||
        buildAvatarDataUri(name),
      rating: rating ?? "New",
      reviews: reviewCount ?? 0,
      experience:
        experience !== null
          ? `${experience} year${experience === 1 ? "" : "s"} experience`
          : "Experience not provided",
      years: formatTeacherYearLabels(
        teacher?.year_labels?.length ? teacher.year_labels : teacher?.year
      ),
      description:
        String(
          teacher?.about ||
            teacher?.bio ||
            teacher?.specializations ||
            teacher?.subjects ||
            ""
        ).trim() || "Tutor information will appear here when provided.",
      qualifications: toList(teacher?.qualifications),
      specializations: toList(teacher?.specializations),
      topicsTaught: toList(teacher?.subjects),
      price: pricePerSession,
      priceMinorUnits: normalizeAudPriceToMinorUnits(rawPricePerSession),
      yearIds: Array.isArray(teacher?.year)
        ? teacher.year
            .map((entry) => Number(entry?.id ?? entry))
            .filter((entry) => Number.isFinite(entry) && entry > 0)
        : [],
    };
  }, [tutorDetailQuery.data, tutorId]);

  const availability = useMemo(
    () =>
      mergePendingTutorSlotsFromStorage({
        tutorId,
        availabilitySlots: normalizeAvailabilityCollection(
          tutorAvailabilityQuery.data
        ),
      }).filter((slot) => isFutureAvailabilitySlot(slot)),
    [tutorAvailabilityQuery.data, tutorId]
  );

  const handleBookSession = () => {
    navigate(`${bookingBasePath}/booking`, {
      state: {
        tutorId,
        tutor,
      },
    });
  };

  return (
    <PageContainer variant={isParentFlow ? "parent" : "default"}>
      <div
        className={`sticky top-0 z-50 ${
          isParentFlow
            ? "bg-[#f8f3ea]/95"
            : "bg-[#DFF2EB]/70"
        } backdrop-blur-md`}
      >
        <Header
          backLabel={isParentFlow ? "Back to Browse Tutors" : "Back to Browse"}
          parentLogoutTone={isParentFlow ? "violet" : "orange"}
        />
      </div>

      <MainContent variant={isParentFlow ? "parent" : "default"}>
        {tutorDetailQuery.isLoading ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-gray-600 shadow-md">
            Loading tutor details...
          </div>
        ) : tutorDetailQuery.isError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-10 text-center text-red-700 shadow-md">
            {getClassBookingQueryErrorMessage(tutorDetailQuery.error, "tutor-detail")}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-10">
            <div className="col-span-2">
              <TutorProfileCard tutor={tutor} />
              <QualificationsCard qualifications={tutor.qualifications} />
              <SpecializationsCard specializations={tutor.specializations} />
            </div>

            <div className="col-span-1">
              <TutorSidebar
                tutor={tutor}
                topicsTaught={tutor.topicsTaught}
                availability={availability}
                onBookSession={handleBookSession}
              />
            </div>
          </div>
        )}
      </MainContent>
    </PageContainer>
  );
};

export default TutorDetails;
